import React, { useState } from 'react';
import styled from 'styled-components';
import dropdownIcon from '../../../assets/icons/dropdown.svg';
import searchIcon from '../../../assets/icons/search.svg';

interface LanguageType {
  value: string;
  text: {
    default: string;
  };
}

interface Props {
  question: {
    languageExpression: string;
    genericText: string;
    data: any;
    value: any;
    avialableLocales: LanguageType[];
    baseLocales: LanguageType[];
    userLang: string | null;
  };
}

const Mask = styled.div<{ isShow: boolean }>`
  display: none;
  min-width: 100vw;
  min-height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  background-color: transparent;
  opacity: 0.3;
  z-index: 10;

  ${({ isShow }) =>
    isShow &&
    `
  display: block;
  `}
`;

const Wrapper = styled.div<{ isOpen: boolean }>`
  z-index: 11;
  position: relative;
  min-height: 50px;
  width: 100%;
  padding: 0 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #c7cad1;
  border-radius: 4px;
  background-color: white;
  font-size: 1rem;
  color: #353531;

  ${({ isOpen }) =>
    isOpen &&
    `
    border: 2px solid #1656E9;
    `}
`;

const Placeholder = styled.p`
  user-select: none;
`;

const Content = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  width: 100%;
  z-index: 202;
  background-color: #fff;
  box-shadow: 0px 2px 4px rgba(63, 63, 68, 0.041958),
    0px 1px 1px rgba(63, 63, 68, 0.0769231);
  border: 1px solid #c7cad1;
  border-radius: 4px;
  list-style-type: none;
  margin: 0;
  padding: 0;
  user-select: none;
`;

const Ul = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  max-height: 14rem;
  overflow-y: scroll;
`;
const Li = styled.li`
  padding: 0.75rem 0 0.75rem 1rem;
  cursor: pointer;
  &:hover {
    background-color: #f1f2f3;
  }
`;

const SeachBox = styled.div`
  display: flex;
  align-items: center;
  margin: 1rem;
  border: 1px solid #c7cad1;
  border-radius: 4px;
  height: 2.5rem;
  padding: 0 0.5rem;
`;

const Input = styled.input`
  border: none !important;
  outline: none !important;
  padding-left: 0.5rem !important;
`;

const DropdownIcon = styled.img<{ isOpen: boolean }>`
  transition: 0.3s;
  user-select: none;
  height: 2rem;
  ${({ isOpen }) =>
    isOpen &&
    `
    transform: rotate(180deg);
  `}
`;

const translateLocales = (lang: string) => {
  const langWithoutRegion = lang.split('-')[0];

  switch (langWithoutRegion) {
    case 'pl':
      return {
        selectText: 'Wybierz język',
        searchText: 'Szukaj...',
      };
    case 'de': {
      return {
        selectText: 'Sprache auswählen',
        searchText: 'Suche...',
      };
    }
    case 'el': {
      return {
        selectText: 'Επιλέξτε γλώσσα',
        searchText: 'Αναζήτηση...',
      };
    }
    case 'fr': {
      return {
        selectText: 'Choisir la langue',
        searchText: 'Chercher...',
      };
    }
    case 'it': {
      return {
        selectText: 'Seleziona la lingua',
        searchText: 'Ricerca...',
      };
    }
    case 'ru': {
      return {
        selectText: 'Выберите язык',
        searchText: 'Поиск...',
      };
    }
    case 'en': {
      return {
        selectText: 'Select language',
        searchText: 'Search...',
      };
    }
    default: {
      return {
        selectText: 'Select language',
        searchText: 'Search...',
      };
    }
  }
};

const SearchIcon = styled.img``;

const sortLocales = (
  arrayToSort: LanguageType[],
  sorterArray: LanguageType[]
) => {
  if (!sorterArray) {
    return arrayToSort;
  }

  const sorted: LanguageType[] = [];

  arrayToSort.forEach((locale: LanguageType) => {
    const shouldBeSorted = sorterArray.find(
      (localeSorted: LanguageType) => localeSorted.value === locale.value
    );

    if (shouldBeSorted) {
      sorted.unshift(locale);
    } else {
      sorted.push(locale);
    }
  });

  return sorted;
};

export const LocaleQuestion = ({ question }: Props) => {
  const [searchValue, search] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const { value, avialableLocales, baseLocales, userLang } = question;

  const sortedArray = sortLocales(avialableLocales, baseLocales);

  const changeLocale = (locale: string) => {
    question.value = locale;
    question.data.locale = locale.toLowerCase();
    if (question.data.adjustSettedLocale) {
      question.data.adjustSettedLocale();
    }

    setIsOpen(false);
  };

  const filteredLocales = sortedArray.filter(
    (locale: LanguageType) =>
      locale.text.default.toLowerCase().indexOf(searchValue.toLowerCase()) > -1
  );

  const userBrowserLanguage = userLang ? userLang : navigator.language;
  const translatations = translateLocales(userBrowserLanguage);

  const humanizedValue = value
    ? avialableLocales.find((locale: LanguageType) => locale.value === value)
        ?.text.default
    : translatations.selectText;

  return (
    <div className="locale-container">
      <Mask isShow={isOpen} onClick={() => setIsOpen(false)} />
      <Wrapper
        isOpen={isOpen}
        data-wrap="wrapper-locale"
        onClick={(e: any) => {
          const id = e.target.dataset.wrap;

          if (id === 'wrapper-locale') {
            setIsOpen(!isOpen);
          }
        }}
      >
        <Placeholder data-wrap="wrapper-locale">{humanizedValue}</Placeholder>
        <DropdownIcon
          isOpen={isOpen}
          src={dropdownIcon}
          onClick={() => setIsOpen(!isOpen)} // <-- Event listener added here
        />
        {isOpen && (
          <Content>
            <SeachBox>
              <SearchIcon src={searchIcon} />
              <Input
                type="text"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  search(e.target.value)
                }
                placeholder={translatations.searchText}
                value={searchValue}
              />
            </SeachBox>
            <Ul>
              {filteredLocales &&
                filteredLocales.map((locale: LanguageType) => (
                  <Li
                    onClick={() => changeLocale(locale.value)}
                    key={locale.value}
                  >
                    {locale.text.default}
                  </Li>
                ))}
            </Ul>
          </Content>
        )}
      </Wrapper>
    </div>
  );
};

